import { Check as CheckIcon, Image as ImageIcon } from '@mui/icons-material';
import { Avatar, Box, Card, Chip, Stack, Typography } from '@mui/material';
import { ReactElement, useMemo } from 'react';

import { CardPartOrganization } from '@app/components/Product/CardPartOrganization';
import { CardPartTitle } from '@app/components/Product/CardPartTitle';
import { AttributeGroupName, Product } from '@app/types/catalog';

interface ResultItemCardProps {
  favoriteSearch?: boolean;
  onClick?: () => void;
  product: Product;
}

export function ResultItemCard({
  onClick,
  product,
}: ResultItemCardProps): ReactElement {
  const tags = [
    product.customFields.commutingCost ? '交通費支給' : '',
    product.variants.some((p) => p.description) ? 'インセンティブ' : '',
  ].filter((p) => p);

  const organization = useMemo(() => {
    return typeof product.organization === 'object'
      ? { ...product.organization }
      : null;
  }, [product.organization]);

  const clinicalDepartmentLabel = useMemo(() => {
    return product.attributes
      .filter((a) => a.groupName === AttributeGroupName.CLINICAL_DEPARTMENT)
      .map((a) => a.items[0].value)
      .join('・');
  }, [product.attributes]);

  const jobTypeLabel = useMemo(() => {
    return product.attributes
      .filter((a) => a.groupName === AttributeGroupName.JOB_TYPE)
      .map((a) => a.name)
      .join('・');
  }, [product.attributes]);

  return (
    <Card
      sx={{
        borderRadius: 0,
        cursor: onClick ? 'pointer' : 'default',
      }}
      data-e2e="searchResult-listItem"
      onClick={onClick}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <CardPartTitle product={product} />
        <Stack spacing={1} direction="row">
          <Box sx={{ width: 1 }}>
            <Typography variant="body2">{jobTypeLabel}</Typography>
            <Typography variant="body2">{clinicalDepartmentLabel}</Typography>
            <Box display="flex" flexWrap="wrap" columnGap={0.5} rowGap={0.5}>
              {tags.map((tag, index) => (
                <Chip
                  key={index}
                  color="primary"
                  icon={<CheckIcon />}
                  label={tag}
                  size="small"
                />
              ))}
            </Box>
          </Box>
          <Box>
            {organization && organization.iconImageUri ? (
              <Box sx={{ ml: 'auto' }}>
                <img
                  alt={organization.name}
                  src={organization.iconImageUri}
                  style={{ width: 106 }}
                />
              </Box>
            ) : (
              <Avatar
                color="primary"
                variant="square"
                sx={{
                  bgcolor: 'secondary.main',
                  height: 106,
                  ml: 'auto',
                  width: 106,
                }}
              >
                <ImageIcon sx={{ color: 'primary.main', opacity: 0.5 }} />
              </Avatar>
            )}
          </Box>
        </Stack>
        {organization && <CardPartOrganization organization={organization} />}
      </Stack>
    </Card>
  );
}
